import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowDownToLine, Share, PlusSquare, Check } from "lucide-react";
import { ChainFlowLogo } from "../brand/ChainFlowLogo";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow OS — Premium Install Prompt
   --------------------------------------------------------------------
   • Listens for the standard `beforeinstallprompt` (Android, Edge,
     desktop Chrome). Stashes the deferred event so we can present a
     branded card instead of the browser's default UI.
   • iOS Safari: no programmatic install API, so we show a calm
     instructional sheet (Share → Add to Home Screen) when we detect
     iOS and not already running standalone.
   • Persistence: a "Don't show again" choice + a 7-day soft snooze on
     dismiss are stored in localStorage so we never nag.
   ─────────────────────────────────────────────────────────────────── */

const LS_DISMISS = "cf:install:dismissed_until";
const LS_NEVER   = "cf:install:never";
const LS_FIRST_SEEN = "cf:install:first_seen_at";
const SHOW_DELAY_MS = 35_000; // wait until the user has actually engaged

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !/(CriOS|FxiOS|EdgiOS|OPiOS)/.test(navigator.userAgent);

const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone === true);

const isSnoozed = () => {
  if (localStorage.getItem(LS_NEVER) === "1") return true;
  const until = parseInt(localStorage.getItem(LS_DISMISS) || "0", 10);
  return Number.isFinite(until) && until > Date.now();
};

const Glyph = () => (
  <span className="shrink-0">
    <ChainFlowLogo variant="icon" size={40} testid="install-prompt-glyph" />
  </span>
);

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;

    // Mark first-seen so we can show the prompt only after meaningful use.
    if (!localStorage.getItem(LS_FIRST_SEEN)) {
      localStorage.setItem(LS_FIRST_SEEN, String(Date.now()));
    }
    const firstSeen = parseInt(localStorage.getItem(LS_FIRST_SEEN), 10) || Date.now();
    const remaining = Math.max(0, firstSeen + SHOW_DELAY_MS - Date.now());

    let timer = null;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (isSnoozed()) return;
      timer = window.setTimeout(() => setOpen(true), remaining);
    };

    const onAppInstalled = () => {
      setOpen(false);
      setShowIOS(false);
      setInstalled(true);
      window.setTimeout(() => setInstalled(false), 3200);
      // Future visits: never re-prompt
      localStorage.setItem(LS_NEVER, "1");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    // iOS path — no event ever fires; surface the card after the same delay
    if (isIOS() && !isSnoozed()) {
      timer = window.setTimeout(() => setShowIOS(true), remaining);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    setOpen(false);
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        // appinstalled event will fire and set NEVER
      } else {
        // Snooze for 7 days
        localStorage.setItem(LS_DISMISS, String(Date.now() + 7 * 24 * 3600 * 1000));
      }
    } catch {
      /* noop */
    } finally {
      setDeferred(null);
    }
  };

  const dismiss = (forever = false) => {
    setOpen(false);
    setShowIOS(false);
    if (forever) {
      localStorage.setItem(LS_NEVER, "1");
    } else {
      localStorage.setItem(LS_DISMISS, String(Date.now() + 7 * 24 * 3600 * 1000));
    }
  };

  if (typeof document === "undefined") return null;

  /* ─── Success toast ─────────────────────────────────────────── */
  if (installed) {
    return createPortal(
      <div
        className="fixed left-1/2 z-[120]"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
          transform: "translateX(-50%)",
        }}
      >
        <div
          role="status"
          aria-live="polite"
          data-testid="cf-install-success"
          className="px-4 py-3 rounded-xl flex items-center gap-2.5 text-white text-[12.5px] font-semibold cf-fade-up"
          style={{
            background:
              "linear-gradient(135deg, rgba(15,17,38,0.96) 0%, rgba(20,18,52,0.96) 100%)",
            border: "1px solid rgba(139,92,246,0.32)",
            boxShadow:
              "0 18px 36px -12px rgba(0,0,0,0.55), 0 0 0 1px rgba(139,92,246,0.10), 0 0 24px rgba(139,92,246,0.30)",
            backdropFilter: "blur(14px) saturate(160%)",
            WebkitBackdropFilter: "blur(14px) saturate(160%)",
          }}
        >
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              boxShadow: "0 0 0 1px rgba(16,185,129,0.32)",
            }}
          >
            <Check size={14} className="text-white" />
          </span>
          ChainFlow OS installed — open it from your home screen
        </div>
      </div>,
      document.body,
    );
  }

  if (!open && !showIOS) return null;

  /* ─── Premium install card ──────────────────────────────────── */
  const card = (
    <div
      className="fixed left-1/2 z-[100] w-[min(420px,calc(100vw-24px))]"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
        transform: "translateX(-50%)",
      }}
    >
      <div
        role="dialog"
        aria-label="Install ChainFlow OS"
        data-testid="cf-install-prompt"
        className="relative rounded-2xl overflow-hidden p-4 sm:p-5 cf-fade-up"
        style={{
          background:
            "linear-gradient(160deg, rgba(15,17,38,0.96) 0%, rgba(20,18,52,0.94) 55%, rgba(12,12,30,0.96) 100%)",
          border: "1px solid rgba(139,92,246,0.28)",
          boxShadow:
            "0 28px 60px -18px rgba(0,0,0,0.65), 0 0 0 1px rgba(139,92,246,0.12), 0 0 32px rgba(124,58,237,0.18)",
          backdropFilter: "blur(20px) saturate(160%)",
          WebkitBackdropFilter: "blur(20px) saturate(160%)",
        }}
      >
        {/* Soft violet bloom */}
        <span
          aria-hidden="true"
          className="absolute -top-16 -right-12 w-44 h-44 rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(139,92,246,0.40), transparent 70%)" }}
        />
        <span
          aria-hidden="true"
          className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(56,189,248,0.18), transparent 70%)" }}
        />

        <div className="relative flex items-start gap-3">
          <Glyph />
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-300">
              ChainFlow OS · Mobile
            </p>
            <h3 className="font-display text-[16px] font-bold text-white tracking-tight mt-1 leading-tight">
              Install ChainFlow OS
            </h3>
            <p className="text-[12.5px] text-slate-300 mt-1 leading-snug">
              Access your transaction pipeline instantly from your home screen.
            </p>
          </div>
          <button
            type="button"
            onClick={() => dismiss(false)}
            aria-label="Dismiss"
            className="w-8 h-8 -mt-1 -mr-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] flex items-center justify-center shrink-0 transition-colors"
            data-testid="cf-install-close"
          >
            <X size={14} />
          </button>
        </div>

        {/* iOS instructional row */}
        {showIOS && !open && (
          <div
            className="relative mt-3 rounded-xl px-3 py-2.5 flex items-center gap-3 text-[11.5px] text-slate-300"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
            data-testid="cf-install-ios-steps"
          >
            <Share size={14} className="text-violet-300 shrink-0" />
            <span>
              Tap <span className="font-semibold text-white">Share</span>
            </span>
            <span className="text-slate-500">→</span>
            <PlusSquare size={14} className="text-violet-300 shrink-0" />
            <span>
              <span className="font-semibold text-white">Add to Home Screen</span>
            </span>
          </div>
        )}

        <div className="relative mt-4 flex items-center gap-2">
          {/* Native-prompt path */}
          {open && (
            <button
              type="button"
              onClick={handleInstall}
              data-testid="cf-install-cta"
              className="flex-1 h-11 rounded-xl text-[13px] font-bold text-white inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.01] active:scale-[0.99]"
              style={{
                background:
                  "linear-gradient(135deg, #7C3AED 0%, #6D28D9 50%, #5B21B6 100%)",
                boxShadow:
                  "0 14px 28px -10px rgba(124,58,237,0.85), inset 0 1px 0 rgba(255,255,255,0.22), 0 0 0 1px rgba(139,92,246,0.30)",
              }}
            >
              <ArrowDownToLine size={14} strokeWidth={2.4} />
              Install
            </button>
          )}
          {/* Don't show again */}
          <button
            type="button"
            onClick={() => dismiss(true)}
            data-testid="cf-install-never"
            className="h-11 px-3 rounded-xl text-[12px] font-semibold text-slate-400 hover:text-slate-200 hover:bg-white/[0.04] transition-colors"
          >
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(card, document.body);
};

export default InstallPrompt;
